import React, { useState, useEffect, useRef } from 'react';
import { X, Trash2 } from 'lucide-react';
import { useDashboard } from '../DashboardContext';

/* Rename or delete a saved plan.
   Advisors manage any plan belonging to the selected client. Clients manage
   their own options only — the plan their advisor authored is the benchmark
   and the server rejects both operations on it (403). */
export default function PlanManage({ plan, onClose }) {
  const { planMgr } = useDashboard();
  const [name, setName] = useState(plan?.name || '');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.select(), 30);
    return () => clearTimeout(t);
  }, []);

  if (!plan) return null;

  const { renamePlan, removePlan, canEditPlan, isClient, plans } = planMgr;
  const editable = canEditPlan(plan);
  const isAdvisorPlan = plan.created_by_role === 'advisor';

  const doRename = async () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === plan.name) return;
    setError(''); setBusy(true);
    try {
      await renamePlan(plan.id, trimmed);
      onClose();
    } catch (e) {
      setError(e.message || 'Could not rename this plan');
      setBusy(false);
    }
  };

  const doDelete = async () => {
    setError(''); setBusy(true);
    try {
      await removePlan(plan.id);
      onClose();
    } catch (e) {
      setError(e.message || 'Could not delete this plan');
      setBusy(false);
    }
  };

  const label = { fontSize: 11, color: '#525252', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' };
  const input = { width: '100%', boxSizing: 'border-box', background: '#FFFFFF', border: '1px solid #D4D4D4', borderRadius: 4, padding: '10px 12px', fontSize: 14, color: '#1A1A1A' };

  return (
    <div className="no-print" role="dialog" aria-modal="true" aria-labelledby="plan-manage-title"
      style={{
        position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(26,26,26,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}>
      <div style={{
        width: '100%', maxWidth: 460, background: '#FFFFFF',
        border: '1px solid #D4D4D4', borderLeft: '6px solid #92700C',
        borderRadius: 8, padding: '26px 30px', fontFamily: "'Outfit', sans-serif",
      }}>
        <div className="flex items-start justify-between gap-4 mb-1">
          <h3 id="plan-manage-title" className="font-semibold" style={{ color: '#1A1A1A', fontSize: 18 }}>
            {plan.name}
          </h3>
          <button onClick={onClose} aria-label="Close"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#737373', padding: 4 }}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs mb-5" style={{ color: '#737373' }}>
          {isAdvisorPlan ? 'Built by MinerOps' : 'Created by the client'}
          {plan.created_by_name ? ` · ${plan.created_by_name}` : ''}
        </p>

        {!editable ? (
          <div className="text-sm p-3" style={{ background: '#F5F5F5', borderLeft: '6px solid #92700C', borderRadius: '0 4px 4px 0', color: '#333' }}>
            This is the plan your advisor built for you. It stays as your benchmark, so it cannot be renamed
            or deleted here. Your own options can be.
          </div>
        ) : (
          <>
            {/* Rename */}
            <label htmlFor="plan-name" style={label}>Plan name</label>
            <input id="plan-name" ref={inputRef} value={name} disabled={busy}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') doRename(); }}
              style={{ ...input, marginBottom: 14 }}
              onFocus={e => { e.target.style.borderColor = '#92700C'; }}
              onBlur={e => { e.target.style.borderColor = '#D4D4D4'; }}
            />
            <button onClick={doRename} disabled={busy || !name.trim() || name.trim() === plan.name}
              className="px-4 py-2 text-sm font-medium mb-6"
              style={{
                background: busy || !name.trim() || name.trim() === plan.name ? '#E5E5E5' : '#1A1A1A',
                color: busy || !name.trim() || name.trim() === plan.name ? '#999999' : '#FFF',
                border: 'none', borderRadius: 4,
                cursor: busy || name.trim() === plan.name ? 'default' : 'pointer',
              }}>
              Rename
            </button>

            {/* Delete */}
            <div className="pt-5" style={{ borderTop: '1px solid #E5E5E5' }}>
              <h4 className="text-sm font-semibold mb-1" style={{ color: '#B91C1C' }}>Delete this plan</h4>
              <p className="text-xs mb-3" style={{ color: '#737373' }}>
                {isClient
                  ? 'Removes this option and everything in it. Your advisor’s plan is unaffected.'
                  : 'Removes the plan from this client’s dashboard. If they built options from it, those stay, as they are separate plans.'}
                {' '}This cannot be undone. Type the plan name to confirm.
              </p>
              <div className="flex gap-2 flex-wrap">
                <input value={confirm} onChange={e => setConfirm(e.target.value)} placeholder={plan.name}
                  disabled={busy} style={{ ...input, maxWidth: 220 }} />
                <button onClick={doDelete} disabled={busy || confirm.trim() !== plan.name}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium"
                  style={{
                    background: confirm.trim() === plan.name ? '#B91C1C' : '#E5E5E5',
                    color: confirm.trim() === plan.name ? '#FFF' : '#999999',
                    border: 'none', borderRadius: 4,
                    cursor: confirm.trim() === plan.name && !busy ? 'pointer' : 'default',
                  }}>
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
              {plans.length === 1 && (
                <p className="text-xs mt-2" style={{ color: '#737373' }}>
                  This is the only saved plan for this dashboard.
                </p>
              )}
            </div>
          </>
        )}

        {error && (
          <div className="text-xs mt-4 px-3 py-2" style={{ background: '#F5F5F5', borderLeft: '6px solid #B91C1C', borderRadius: '0 4px 4px 0', color: '#333' }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
